<script setup lang="ts">
import { type Component, type ComponentPublicInstance, computed, onUnmounted, ref, useSlots, watch } from 'vue';
import type { Placement } from '@/modules/common/types/placement.ts';
import { PointerType } from '@/modules/common/enums/pointer-type.ts';
import { useTooltipManager } from '@/modules/ui/composables/use-tooltip-manager.ts';
import {
  HINT_TOOLTIP_OPEN_DELAY,
  TOOLTIP_INTERACTIVE_CLOSE_DELAY,
  TOOLTIP_OPEN_DELAY,
} from '@/modules/common/consts/ui.ts';
import UiTooltipContent from '@/modules/ui/components/ui/UiTooltipContent.vue';

const {
  is = 'div',
  text,
  placement = 'bottom',
  maxWidth = 400,
  disabled,
  onlyMouse,
  align = 'center',
  altBoundary = false,
  variant = 'default',
  overlay,
  overlayAlign = 'start',
  overlayLabel,
  reference,
  offset,
  interactive = false,
  openDelay,
} = defineProps<{
  is?: string | Component;
  text?: string;
  placement?: Placement;
  maxWidth?: number;
  disabled?: boolean;
  onlyMouse?: boolean;
  align?: 'center' | 'left';
  altBoundary?: boolean;
  variant?: 'default' | 'hint';
  overlay?: boolean;
  overlayAlign?: 'start' | 'end';
  overlayLabel?: string;
  reference?: HTMLElement | ComponentPublicInstance | null;
  offset?: number;
  title?: string;
  interactive?: boolean;
  openDelay?: number;
}>();

const slots = useSlots();

const anchorRef = ref<HTMLElement | ComponentPublicInstance>();
const floatingAnchor = computed(() => reference ?? anchorRef.value);

const tooltipId = Symbol('ui-tooltip');
const { activeTooltipId } = useTooltipManager();

const isActive = computed(() => activeTooltipId.value === tooltipId);

let rafId: number | null = null;
let openTimerId: number | null = null;
let closeTimerId: number | null = null;

const openDelayMs = computed(() => {
  if (typeof openDelay === 'number') {
    return openDelay;
  }

  return variant === 'hint' ? HINT_TOOLTIP_OPEN_DELAY : TOOLTIP_OPEN_DELAY;
});

const show = computed(() => !disabled && !!(text || slots.tooltip) && isActive.value);

const isRendered = ref(false);

watch(show, (show) => {
  if (show) {
    isRendered.value = true;
  }
});

const overlayFontStyle = ref<Record<string, string>>({});

function captureOverlayFont() {
  if (!overlay) {
    return;
  }

  const el = anchorRef.value instanceof Element ? anchorRef.value : anchorRef.value?.$el;

  if (!(el instanceof Element)) {
    return;
  }

  const s = getComputedStyle(el);

  overlayFontStyle.value = {
    fontFamily: s.fontFamily,
    fontSize: s.fontSize,
    fontWeight: s.fontWeight,
    fontStyle: s.fontStyle,
    letterSpacing: s.letterSpacing,
  };
}

const textStyle = computed(() => ({
  textAlign: align,
  ...(overlay ? { whiteSpace: 'nowrap', ...overlayFontStyle.value } : {}),
}));

function cancelOpenTimer() {
  if (openTimerId != null) {
    clearTimeout(openTimerId);
    openTimerId = null;
  }
}

function cancelCloseTimer() {
  if (closeTimerId != null) {
    clearTimeout(closeTimerId);
    closeTimerId = null;
  }
}

function cancelRaf() {
  if (rafId == null) {
    return;
  }

  cancelAnimationFrame(rafId);
  rafId = null;
}

function closeTooltip() {
  if (isActive.value) {
    activeTooltipId.value = null;
  }
}

watch(
  () => disabled,
  (disabled) => {
    if (!disabled) {
      return;
    }

    cancelOpenTimer();
    cancelCloseTimer();
    cancelRaf();
    closeTooltip();
  }
);

function schedule(action: 'open' | 'close') {
  if (action === 'open') {
    cancelRaf();
    cancelCloseTimer();

    if (isActive.value || openTimerId != null) {
      return;
    }

    openTimerId = window.setTimeout(() => {
      openTimerId = null;
      captureOverlayFont();
      activeTooltipId.value = tooltipId;
    }, openDelayMs.value);

    return;
  }

  cancelOpenTimer();

  if (!isActive.value || rafId != null || closeTimerId != null) {
    return;
  }

  if (interactive) {
    closeTimerId = window.setTimeout(() => {
      closeTimerId = null;
      closeTooltip();
    }, TOOLTIP_INTERACTIVE_CLOSE_DELAY);
    return;
  }

  rafId = requestAnimationFrame(() => {
    rafId = null;
    closeTooltip();
  });
}

function onContentEnter() {
  cancelRaf();
  cancelCloseTimer();
}

function onContentLeave() {
  schedule('close');
}

function onPointerEnter(e: PointerEvent) {
  e.stopPropagation();

  if (e.pointerType === PointerType.Mouse) {
    schedule('open');
  } else {
    if (!onlyMouse) {
      captureOverlayFont();
      activeTooltipId.value = tooltipId;
    }
  }
}

function onPointerMove(e: PointerEvent) {
  e.stopPropagation();

  if (!isActive.value) {
    onPointerEnter(e);
  }
}

function onPointerLeave(e: PointerEvent) {
  e.stopPropagation();
  if (e.pointerType !== PointerType.Mouse) {
    return;
  }

  schedule('close');
}

function onWindowPointerDown(e: PointerEvent) {
  if (!anchorRef.value || !(e.target instanceof Element)) {
    return;
  }

  cancelRaf();
  cancelOpenTimer();
  cancelCloseTimer();

  if (interactive && e.target.closest('[data-ui-tooltip-content]')) {
    return;
  }

  const el = anchorRef.value instanceof Element ? anchorRef.value : anchorRef.value.$el;

  if (!el.contains(e.target)) {
    closeTooltip();
  }
}

watch(isActive, (active) => {
  if (active) {
    window.addEventListener('pointerdown', onWindowPointerDown);
  } else {
    window.removeEventListener('pointerdown', onWindowPointerDown);
  }
});

onUnmounted(() => {
  cancelRaf();
  cancelOpenTimer();
  cancelCloseTimer();

  if (isActive.value) {
    activeTooltipId.value = null;
  }

  window.removeEventListener('pointerdown', onWindowPointerDown);
});
</script>

<template>
  <component
    :is="is"
    ref="anchorRef"
    class="ui-tooltip"
    :class="{ _disabled: disabled }"
    @pointerenter="onPointerEnter"
    @pointermove="onPointerMove"
    @pointerleave="onPointerLeave"
  >
    <slot />

    <UiTooltipContent
      v-if="isRendered"
      :anchor="floatingAnchor"
      :show="show"
      :placement="placement"
      :max-width="maxWidth"
      :alt-boundary="altBoundary"
      :variant="variant"
      :overlay="overlay"
      :overlay-align="overlayAlign"
      :offset="offset"
      :interactive="interactive"
      @after-leave="isRendered = false"
      @content-enter="onContentEnter"
      @content-leave="onContentLeave"
    >
      <slot name="tooltip">
        <p
          v-if="text"
          class="text"
          :style="textStyle"
          ><span
            v-if="overlay && overlayLabel"
            class="overlay-label"
            >{{ overlayLabel }}</span
          >{{ $maybeT(text) }}</p
        >
      </slot>
    </UiTooltipContent>
  </component>
</template>

<style scoped lang="scss">
.text {
  text-align: center;
  white-space: pre-line;
  word-break: break-word;
  color: var(--color-gray-1000);
  font-size: var(--font-size);
  line-height: var(--line-height);
  font-weight: var(--font-weight);
}
</style>
